import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, Tag, TrendingUp, User } from 'lucide-react';
import { getArticleBySlug, getRelatedArticles, getWriters } from '@/lib/api';
import { useSEO } from '@/lib/useSEO';

// Bormula Logo URL
const BORMULA_LOGO = "https://customer-assets.emergentagent.com/job_a6e857fc-9f8a-42a4-a234-830c8a3cc714/artifacts/98ozj9ol_Bormula%20Live%20Logo%201080x1350%20%281%29.png";

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [writerSlug, setWriterSlug] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchArticle = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getArticleBySlug(slug);
      setArticle(data);
      
      // Fetch related articles (scored by shared tags + category)
      try {
        const related = await getRelatedArticles(slug, 3);
        setRelatedArticles(related);
      } catch (e) {
        setRelatedArticles([]);
      }

      // Resolve writer slug if article has an author_id
      if (data.author_id) {
        try {
          const allWriters = await getWriters();
          const matched = allWriters.find(w => w.id === data.author_id);
          if (matched) setWriterSlug(matched.slug);
        } catch (e) {
          // ignore writer lookup failure
        }
      }
    } catch (err) {
      setError('Article not found');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchArticle();
    window.scrollTo(0, 0);
  }, [fetchArticle]);

  useSEO(article ? {
    title: article.title,
    description: article.excerpt,
    image: article.image_url,
    url: 'https://bormula.com/article/' + article.slug,
    type: 'article',
    author: article.author_name,
  } : {});

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const categoryColors = {
    news: 'bg-[#CCFF00] text-black',
    memes: 'bg-purple-500 text-white',
    'race-updates': 'bg-red-500 text-white',
    reels: 'bg-blue-500 text-white',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-6">
        <h1 className="font-heading text-4xl font-black text-white uppercase">Article Not Found</h1>
        <Link 
          to="/"
          className="flex items-center gap-2 text-[#CCFF00] hover:underline"
          data-testid="back-home-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="article-page">
      {/* Navigation Bar */}
      <nav className="fixed top-0 w-full z-50 bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src={BORMULA_LOGO} alt="Bormula" className="h-10 w-10 object-contain" />
              <span className="font-heading text-2xl font-black text-white tracking-tighter">BORMULA</span>
            </Link>
            <Link 
              to="/"
              className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-white/80 hover:text-[#CCFF00] transition-colors"
              data-testid="back-link"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Image */}
      <div className="relative h-[50vh] min-h-[400px] overflow-hidden pt-16">
        <img 
          src={article.image_url || 'https://images.unsplash.com/photo-1749952648908-d20b76c078dc'}
          alt={article.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
      </div>

      {/* Article Content */}
      <article className="relative -mt-32 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <header className="mb-12">
            {/* Category */}
            <span className={`inline-block px-4 py-2 text-xs font-bold uppercase tracking-wider mb-6 ${categoryColors[article.category] || categoryColors.news}`}>
              {article.category}
            </span>

            {/* Title */}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-white leading-tight mb-6" data-testid="article-title">
              {article.title}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-text-secondary">
              {article.author_name && (
                <span className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {writerSlug ? (
                    <Link to={`/writer/${writerSlug}`} className="hover:text-[#CCFF00] transition-colors" data-testid="article-author-link">
                      {article.author_name}
                    </Link>
                  ) : (
                    <span>{article.author_name}</span>
                  )}
                </span>
              )}
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {formatDate(article.created_at)}
              </span>
              {article.trending && (
                <span className="flex items-center gap-2 text-[#CCFF00]">
                  <TrendingUp className="w-4 h-4" />
                  Trending
                </span>
              )}
            </div>
          </header>

          {/* Excerpt */}
          <div className="mb-8 p-6 bg-surface border-l-4 border-[#CCFF00]">
            <p className="font-body text-lg text-white italic">
              {article.excerpt}
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-invert prose-lg max-w-none">
            <div
              className="font-body text-text-secondary leading-relaxed"
              data-testid="article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>

          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-zinc-800">
              <div className="flex items-center gap-2 flex-wrap">
                <Tag className="w-4 h-4 text-text-secondary" />
                {article.tags.map((tag, index) => (
                  <Link 
                    key={index}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="px-3 py-1 bg-surface border border-zinc-800 text-xs font-mono text-text-secondary hover:border-[#CCFF00] hover:text-[#CCFF00] transition-colors"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-24 bg-surface mt-24" data-testid="related-articles">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl font-black uppercase tracking-tighter text-white mb-12">
              Related Stories
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/article/${related.slug}`}
                  className="group"
                  data-testid={`related-article-${related.slug}`}
                >
                  <div className="aspect-video overflow-hidden bg-black mb-4">
                    <img 
                      src={related.image_url || 'https://images.unsplash.com/photo-1749952648908-d20b76c078dc'}
                      alt={related.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <span className="font-mono text-xs text-[#CCFF00] uppercase">{related.category}</span>
                  <h3 className="font-heading text-xl font-bold uppercase text-white group-hover:text-[#CCFF00] transition-colors mt-2">
                    {related.title}
                  </h3>
                  <p className="font-body text-sm text-text-secondary mt-2 line-clamp-2">
                    {related.excerpt}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-12 bg-black border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/" className="inline-flex items-center gap-2 justify-center">
            <img src={BORMULA_LOGO} alt="Bormula" className="h-8 w-8 object-contain" />
            <span className="font-heading text-2xl font-black text-[#CCFF00]">BORMULA</span>
          </Link>
          <p className="font-mono text-xs text-text-secondary mt-2">© 2024</p>
        </div>
      </footer>
    </div>
  );
}
